import { CONTENT_HUB_SOURCES, type ContentHubSource } from "./config";
import type {
  ContentHubItem,
  ContentHubSearchHit,
  ContentHubStreamFrame,
  FinXPayload,
  HeadlinesPayload,
  StocksNewsPayload,
  TruthSocialPayload,
} from "./types";

const asString = (value: unknown): string | null =>
  typeof value === "string" ? value : null;

/** Normalized display fields extracted from a source-specific payload. */
type PayloadFields = {
  title: string;
  body: string | null;
  occursAt: string;
  tickers: string[];
};

const headlinesFields = (raw: Record<string, unknown>): PayloadFields => {
  const payload = raw as Partial<HeadlinesPayload>;
  return {
    title: payload.title ?? "",
    body: payload.body ?? null,
    occursAt: payload.occurs_at ?? "",
    tickers: payload.tickers ?? [],
  };
};

const truthSocialFields = (raw: Record<string, unknown>): PayloadFields => {
  const payload = raw as Partial<TruthSocialPayload>;
  return {
    title: payload.content ?? "",
    body: null,
    occursAt: payload.created_at ?? "",
    tickers: [],
  };
};

const finXFields = (raw: Record<string, unknown>): PayloadFields => {
  const payload = raw as Partial<FinXPayload>;
  return {
    title: payload.text ?? "",
    body: null,
    occursAt: payload.created_at ?? "",
    tickers: [],
  };
};

const stocksNewsFields = (raw: Record<string, unknown>): PayloadFields => {
  const payload = raw as Partial<StocksNewsPayload>;
  return {
    title: payload.title ?? "",
    body: payload.body ?? null,
    occursAt: payload.created ?? "",
    tickers: payload.stocks ?? payload.tickers ?? [],
  };
};

/** Benzinga structured equities feeds (government-trade, option-activity,
 *  insider-transaction, block-trade). The equities table widgets render the
 *  raw payload through their own transformers, so these normalized fields are
 *  only used by the content-hub layer (a display ticker/label). Pull a ticker
 *  and a creation time from the common field names, tolerating the flat and
 *  `data`-nested payload shapes the transformers already handle. */
const datahoseFields = (raw: Record<string, unknown>): PayloadFields => {
  const data = (raw.data as Record<string, unknown>) ?? {};
  const pick = (key: string): string | null =>
    asString(raw[key]) ?? asString(data[key]);
  const ticker =
    pick("ticker") ??
    pick("security_ticker") ??
    pick("company_symbol") ??
    pick("symbol");
  return {
    title:
      pick("security_name") ??
      pick("company_name") ??
      pick("name") ??
      ticker ??
      "",
    body: null,
    occursAt:
      pick("created_utc") ??
      pick("created_at") ??
      pick("transaction_date") ??
      pick("filing_date") ??
      pick("date") ??
      "",
    tickers: ticker ? [ticker] : [],
  };
};

/** Newsquawk calendar events (`newsquawk-calendar-event`). The payload is the
 *  full calendar entry (data/earning/supply/speaker/holiday/…); the calendar-v2
 *  grid reads it wholesale off `item.raw`, so these normalized fields are only a
 *  display fallback (a title + a parseable time) for the generic content-hub
 *  layer. `title`/`summary` cover data-style entries; earnings use `ticker`. */
const calendarEventFields = (raw: Record<string, unknown>): PayloadFields => ({
  title:
    asString(raw.title) ??
    asString(raw.summary) ??
    asString(raw.ticker) ??
    "",
  body: asString(raw.detail),
  occursAt: asString(raw.occurs_at) ?? asString(raw.created_at) ?? "",
  tickers: asString(raw.ticker) ? [asString(raw.ticker) as string] : [],
});

/** 13F filing grain — the secapi filing envelope (minus its holdings). The
 *  institution is the title; the filing date is the display time. */
const sec13fFilingFields = (raw: Record<string, unknown>): PayloadFields => ({
  title: asString(raw.company_name) ?? "",
  body: null,
  occursAt:
    asString(raw.filing_date) ?? asString(raw.period_of_report) ?? "",
  tickers: [],
});

/** 13F holding grain — one secapi position. The held security is the title. */
const sec13fHoldingFields = (raw: Record<string, unknown>): PayloadFields => ({
  title: asString(raw.issuer_name) ?? asString(raw.ticker) ?? "",
  body: null,
  occursAt:
    asString(raw.filing_date) ?? asString(raw.reporting_period) ?? "",
  tickers: asString(raw.ticker) ? [asString(raw.ticker) as string] : [],
});

/** Fallback for sources without a declared payload schema yet. */
const defaultFields = (raw: Record<string, unknown>): PayloadFields => ({
  title: asString(raw.title) ?? "",
  body: asString(raw.body),
  occursAt: asString(raw.created_at) ?? "",
  tickers: [],
});

/** Per-source payload schema mappers. Add an entry here when integrating a
 *  new source; sources without one fall back to defaultFields. */
const payloadFieldMappers: Record<
  ContentHubSource,
  (raw: Record<string, unknown>) => PayloadFields
> = {
  [CONTENT_HUB_SOURCES.HEADLINES]: headlinesFields,
  [CONTENT_HUB_SOURCES.TRUTH_SOCIAL]: truthSocialFields,
  [CONTENT_HUB_SOURCES.FIN_X]: finXFields,
  [CONTENT_HUB_SOURCES.ENERGY_X]: finXFields,
  [CONTENT_HUB_SOURCES.STOCKS_ONE]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.STOCKS_TWO]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.STOCKS_THREE]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.STOCKS_FOUR]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.STOCKS_FOUR_FILINGS]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.STOCKS_FOUR_FEDSPEAK]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.STOCKS_FOUR_GENERAL]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.CRYPTO_WEB]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.FIN_WEB]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.ENERGY_WEB]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.SEC_FILINGS]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.CALENDAR_EVENT]: calendarEventFields,
  [CONTENT_HUB_SOURCES.FINANCIAL_REPORTS_EU]: stocksNewsFields,
  [CONTENT_HUB_SOURCES.GOVERNMENT_TRADE]: datahoseFields,
  [CONTENT_HUB_SOURCES.OPTION_ACTIVITY]: datahoseFields,
  [CONTENT_HUB_SOURCES.INSIDER_TRANSACTION]: datahoseFields,
  [CONTENT_HUB_SOURCES.BLOCK_TRADE]: datahoseFields,
  [CONTENT_HUB_SOURCES.SEC_13F_FILING]: sec13fFilingFields,
  [CONTENT_HUB_SOURCES.SEC_13F_HOLDING]: sec13fHoldingFields,
};

function fromPayload(
  backendSource: string,
  payload: Record<string, unknown>
): PayloadFields {
  const mapper =
    payloadFieldMappers[backendSource as ContentHubSource] ?? defaultFields;
  return mapper(payload);
}

/** Map a POST /v1/search hit into the normalized model. Search hits have no
 *  publish_id — it's constructed from backend_source + source_id. Envelope
 *  fields (occurs_at, tickers, text_1) fill in where present. */
export function mapSearchHit(hit: ContentHubSearchHit): ContentHubItem {
  const payload = hit.payload ?? {};
  const fields = fromPayload(hit.backend_source, payload);
  return {
    publishId: `${hit.backend_source}:${hit.source_id}`,
    backendSource: hit.backend_source,
    sourceId: hit.source_id,
    sourceType: hit.source_type,
    title: fields.title || (hit.text_1 ?? ""),
    body: fields.body,
    // Prefer the envelope time, then the payload time; fall back to
    // indexed_at so every item carries a parseable timestamp even when a
    // source omits its own (e.g. fin-x has a null envelope occurs_at).
    occursAt: hit.occurs_at || fields.occursAt || hit.indexed_at,
    tickers: hit.tickers && hit.tickers.length > 0 ? hit.tickers : fields.tickers,
    raw: payload,
  };
}

/** Map a GET /v1/stream frame into the normalized model. The frame envelope
 *  is thinner than a search hit — the payload is the authoritative content. */
export function mapStreamFrame(frame: ContentHubStreamFrame): ContentHubItem {
  const payload = frame.payload ?? {};
  const fields = fromPayload(frame.backend_source, payload);
  return {
    publishId:
      frame.publish_id || `${frame.backend_source}:${frame.source_id}`,
    backendSource: frame.backend_source,
    sourceId: frame.source_id,
    sourceType: frame.source_type ?? "",
    title: fields.title,
    body: fields.body,
    occursAt: fields.occursAt,
    tickers: fields.tickers,
    raw: payload,
  };
}
